// Temp fix for circular dependency
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';

import { tags } from '~features/tags/tagNames';
import { authApi } from '~services/api/auth';
import { api, apiTags } from '~shared/api/origin';
import { eventsInbox, EventType } from '~shared/services/eventsInbox';
import {
	DatadogLogger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';

import type {
	GetUserFeedbackApiResponse,
	GetUserFeedbackApiResponseTransformed,
	SendUserFeedbackApiRequest,
	TUserConfig,
	TUserExperiments,
	TUserInfo,
	TUserLevel,
	TUserTags,
} from './types';

const usersApi = api.injectEndpoints({
	endpoints: (build) => ({
		userInfo: build.query<TUserInfo, { id: string }>({
			query: ({ id }) => ({ url: `/users/private/${id}` }),
		}),
		userPreferences: build.query<{ language?: string }, { userId: string }>({
			query: ({ userId }) => ({ url: `ai/users/${userId}/preferences` }),
		}),
		setUserLanguage: build.mutation<
			unknown,
			{ userId: string; language: string }
		>({
			query: ({ userId, language }) => ({
				url: `ai/users/${userId}/preferences`,
				method: 'PATCH',
				body: {
					language,
				},
			}),
		}),
		editUserInfo: build.mutation<
			undefined,
			{ id: string; data: Pick<TUserInfo, 'gender' | 'name' | 'birthday'> }
		>({
			query: ({ id, data }) => ({
				url: `/users/${id}`,
				method: 'PATCH',
				body: data,
			}),
			async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
				const infoPatch = dispatch(
					util.updateQueryData('userInfo', { id }, (draft) => {
						draft.gender = data.gender;
						draft.name = data.name;
						draft.birthday = data.birthday;
					})
				);
				try {
					await queryFulfilled;
				} catch (e) {
					DatadogLogger.error(
						LoggerServices.UserService,
						LoggerMessages.UserInfoUpdateError,
						{ error: e }
					);
					infoPatch.undo();
				}
			},
		}),
		tags: build.query<TUserTags, { id: string }>({
			query: ({ id }) => ({ url: `/users/${id}/tags` }),
			async onCacheEntryAdded(
				{},
				{ updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
			) {
				// create subscription
				let unsub = null;
				try {
					await cacheDataLoaded;

					unsub = eventsInbox.onMsg((msg) => {
						switch (msg.type) {
							case EventType.UserTagSet:
								const tag = msg.payload.tag;
								updateCachedData((draft) => {
									draft.push(tag);
								});
								if (tag === tags.subscription) {
									// TODO: Probably does not affect, because needs to be inside dispatch()
									authApi.util.updateQueryData(
										'authenticate',
										undefined,
										(draft) => {
											draft.isSafe = true;
										}
									);
									dispatch(
										api.util.invalidateTags([
											apiTags.subscriptions,
											apiTags.characterGenerationQuota,
											apiTags.imagesGenerationQuota,
											apiTags.videosGenerationQuota,
											apiTags.generatedMedia,
											apiTags.generatedPublicMedia,
											apiTags.gifts,
											apiTags.userConfig,
										])
									);
								}
								if (tag === tags.isSafe) {
									dispatch(
										authApi.util.updateQueryData(
											'authenticate',
											undefined,
											(draft) => {
												draft.isSafe = false;
											}
										)
									);
									dispatch(
										api.util.invalidateTags([apiTags.grid, apiTags.contacts])
									);
								}
								break;

							case EventType.UserTagUnset:
								if (msg.payload?.tag === tags.isSafe) {
									dispatch(
										api.util.invalidateTags([apiTags.grid, apiTags.contacts])
									);
								}
								updateCachedData((draft) => {
									const tag = msg.payload.tag;
									const indexOfTag = draft.indexOf(tag);
									draft.splice(indexOfTag, 1);
								});
								break;

							// tags and experiments are concatenated via useUserTags hook
							case EventType.UserExperimentSet:
								const experimentTag = msg.payload.tag;
								updateCachedData((draft) => {
									if (!draft.includes(experimentTag)) {
										draft.push(experimentTag);
									}
								});
								break;
						}
					});
				} catch (err) {}

				await cacheEntryRemoved;
				unsub?.();
			},
			providesTags: [apiTags.tags],
		}),
		experiments: build.query<TUserExperiments, { id: string }>({
			query: ({ id }) => ({ url: `/users/${id}/tags/experiments` }),
			providesTags: [apiTags.tags],
		}),
		userConfig: build.query<TUserConfig, { userId: string }>({
			query: ({ userId }) => ({ url: `ai/config/${userId}` }),
			providesTags: [apiTags.userConfig],
		}),
		setLastBotId: build.mutation<
			TUserConfig,
			{ userId: string; lastBotId: string }
		>({
			query: ({ userId, lastBotId }) => ({
				url: `ai/users/${userId}/props`,
				method: 'PATCH',
				body: {
					lastBotChatId: lastBotId,
				},
			}),
		}),
		getLevel: build.query<TUserLevel, { userId: string }>({
			async queryFn({ userId }, _queryApi, _extraOptions, fetchWithBQ) {
				const response = await fetchWithBQ(`/ai/users/${userId}/experience`);
				if (response.error && response.error.status !== 400)
					return {
						error: response?.error,
					};
				const data = (response?.data || null) as TUserLevel;
				return {
					data,
				};
			},
		}),
		userWelcomeMessage: build.mutation<
			undefined,
			{ userId: string; botId: string | number }
		>({
			query: ({ userId, botId }) => ({
				url: `ai/chats/${userId}/${botId}/init`,
				method: 'POST',
			}),
		}),
		userDelete: build.mutation<unknown, { userId: string }>({
			query: ({ userId }) => ({
				url: `/users/${userId}`,
				method: 'DELETE',
			}),
		}),
		// sets order of bots to unlock on web
		setSelectedBot: build.mutation<unknown, { id: string }>({
			query: ({ id }) => ({
				url: `ai/users/${id}/onboarding-answers`,
				method: 'PATCH',
				body: {
					selectedBot: 'anna',
				},
			}),
		}),
		// sets default bot preferences during register
		setUserDefaultPreferences: build.mutation<unknown, { id: string }>({
			query: ({ id }) => ({
				url: `ai/users/${id}/preferences`,
				method: 'PATCH',
				body: {
					personality: 'flirty',
					birthday: '1999-12-31T23:00:00Z',
					name: 'Jessica',
					gender: 'fem',
					ethnicity: 'caucasian',
				},
			}),
		}),
		// feedback
		getUserFeedback: build.query<
			GetUserFeedbackApiResponseTransformed,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/users/${userId}/feedback`,
			}),
			transformResponse: (response: GetUserFeedbackApiResponse) => {
				const data = {} as GetUserFeedbackApiResponseTransformed;
				response.categories.forEach((feedback) => {
					data[feedback.category] = feedback;
				});

				return data;
			},
		}),
		sendUserFeedback: build.mutation<
			undefined,
			{ userId: string; body: SendUserFeedbackApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/users/${userId}/feedback`,
				method: 'POST',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					dispatch(
						util.updateQueryData('getUserFeedback', { userId }, (draft) => {
							const current = draft[body.category];
							if (current) {
								current.feedbackCount += 1;
							} else {
								draft[body.category] = {
									category: body.category,
									feedbackCount: 1,
									latestIteration: NaN,
								};
							}
						})
					);
				} catch {}
			},
		}),
		// registration props
		getRegistrationProps: build.query<
			{ location?: string },
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/users/${userId}/registration/props`,
			}),
			providesTags: [apiTags.userProps],
		}),
		updateRegistrationProps: build.mutation<
			{ result: 'success' },
			{ userId: string; location?: string }
		>({
			queryFn: async (
				{ userId, location },
				_queryApi,
				_extraOptions,
				fetchWithBQ
			) => {
				_queryApi.dispatch(api.util.invalidateTags([apiTags.userProps]));
				const result = await _queryApi.dispatch(
					usersApi.endpoints.getRegistrationProps.initiate({ userId })
				);

				if (result.isSuccess && result.data.location === undefined) {
					const { error } = await fetchWithBQ({
						url: `ai/users/${userId}/registration/props`,
						method: 'PATCH',
						body: { location },
					});

					if (error) {
						return {
							error: {
								status: error.status,
								data: error.data,
							} as FetchBaseQueryError,
						};
					}
				}

				return { data: { result: 'success' } };
			},
		}),
	}),
});

export const {
	util,
	endpoints,
	useSetUserLanguageMutation,
	useUserInfoQuery,
	useEditUserInfoMutation,
	useTagsQuery,
	useExperimentsQuery,
	useUserConfigQuery,
	useGetLevelQuery,
	useUserWelcomeMessageMutation,
	useUserDeleteMutation,
	useSetSelectedBotMutation,
	useSetUserDefaultPreferencesMutation,
	useLazyUserPreferencesQuery,
	useSetLastBotIdMutation,
	//feedback
	useGetUserFeedbackQuery,
	useSendUserFeedbackMutation,
	// registration props
	useGetRegistrationPropsQuery,
	useLazyGetRegistrationPropsQuery,
	useUpdateRegistrationPropsMutation,
} = usersApi;
